import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, setAuthToken } from "@/App";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback({ onAuth }) {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));

    // Handle auth errors
    const authError = params.get("auth_error");
    if (authError) {
      setError(authError === "invalid_state" ? "Session expired. Please try again." : `Sign-in error: ${authError}`);
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    // New flow: direct Google OAuth returns auth_token
    const authToken = params.get("auth_token");
    if (authToken) {
      setAuthToken(authToken);
      window.history.replaceState(null, "", window.location.pathname);
      // Fetch user data with the token
      const fetchUser = async () => {
        try {
          const res = await axios.get(`${API}/auth/me`);
          onAuth(res.data);
          navigate("/", { replace: true });
        } catch (e) {
          setError("Failed to load your account. Please try again.");
        }
      };
      fetchUser();
      return;
    }

    // Legacy flow: Emergent auth returns session_id
    const sessionId = params.get("session_id");
    if (sessionId) {
      const exchangeSession = async () => {
        try {
          const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          if (res.data?.session_token) {
            setAuthToken(res.data.session_token);
          }
          onAuth(res.data);
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/", { replace: true });
        } catch (e) {
          const detail = e.response?.data?.detail;
          setError(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
          window.history.replaceState(null, "", window.location.pathname);
        }
      };
      exchangeSession();
      return;
    }

    // No token or session_id found
    navigate("/", { replace: true });
  }, [navigate, onAuth]);

  if (error) {
    const handleRetry = () => {
      window.location.href = `/api/auth/google/login`;
    };
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <p className="text-[#ff6b6b] text-lg font-bold mb-2">Sign-in failed</p>
          <p className="text-[#a3a3a3] text-sm mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <button onClick={handleRetry} className="h-10 px-6 bg-[#ccff00] text-black font-bold text-sm rounded-sm hover:bg-[#b8e600]">TRY AGAIN</button>
            <button onClick={() => { window.history.replaceState(null, "", "/"); window.location.href = "/"; }} className="text-xs text-[#888] hover:text-white">Go to homepage</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#a3a3a3] text-sm">Signing you in...</p>
      </div>
    </div>
  );
}
